import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Search, X, Package as PackageIcon, ArrowRight, CircleDot, Ban, RotateCcw, CalendarDays,
} from "lucide-react";
import MOShell, {
  useMOAccess, isWritable, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import { Field, Input } from "@/pages/masterOrder/MOClients";
import {
  listPackagingItems, createPackagingItem, updatePackagingItem,
  listPackagingPricesByItem, createPackagingPrice,
  PACKAGING_BASE_UNITS, PACKAGING_PURCHASE_UNITS_BY_FAMILY,
  packagingUnitFamily, packagingUnitLabel,
  COMMON_CURRENCIES, mapCostingError,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

const showErr = (e) => toast.error(mapCostingError(e) || formatApiError(e));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("ru-RU") : "—");
const todayISO = () => new Date().toISOString().slice(0, 10);
const n2 = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const nPretty = (v) => {
  // Preserves whole values (10 → "10") and rounds fractions to at most 2 dp with , separator.
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return Number.isInteger(n) ? n.toString() : n.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
};
/** Per-unit price rendered in the user's original purchase unit. */
const unitPriceLabel = (p) => {
  if (!p) return null;
  const total = Number(p.price);
  const qty = Number(p.purchase_quantity);
  if (!(total > 0) || !(qty > 0)) return null;
  const per = total / qty;
  return `${n2(per)} ${p.currency_code} / ${packagingUnitLabel(p.purchase_unit)}`;
};
const purchaseLabel = (p) => p ? `${nPretty(p.price)} ${p.currency_code} за ${nPretty(p.purchase_quantity)} ${packagingUnitLabel(p.purchase_unit)}` : "";

/** Live preview helper: user-facing per-unit cost.
 *  Deliberately renders in the ORIGINAL purchase unit so a user who bought
 *  "8 EUR / 25 m" sees "0.32 EUR / м", not the backend-normalized "/cm" figure.
 */
function livePricePreview({ price, qty, unit, currency }) {
  const p = Number(price); const q = Number(qty);
  if (!Number.isFinite(p) || !Number.isFinite(q) || p <= 0 || q <= 0 || !unit) return null;
  const per = p / q;
  // Decimal-friendly rounding: 4 significant digits for tiny numbers, 2 for the rest.
  const rounded = per < 0.01 ? per.toPrecision(3) : (Math.round(per * 100) / 100).toFixed(2);
  return `${p} ${currency} за ${q} ${packagingUnitLabel(unit)} → ${rounded} ${currency} / ${packagingUnitLabel(unit)}`;
}

/** Deterministic latest-price picker.
 *  Bug fix (2026-02-20): when two prices share the same effective_at, we must
 *  tie-break on created_at (server-assigned monotonic timestamp) so the newly
 *  saved row wins. Old logic used a strict `>` on effective_at only, which kept
 *  the first-iterated row and displayed the stale one when the user added
 *  another price on the same day.
 */
function priceRank(p) {
  return `${p?.effective_at || ""}|${p?.created_at || ""}`;
}
function latestPricesByCurrency(prices) {
  const byCur = new Map();
  for (const p of prices) {
    const cur = p.currency_code;
    const prev = byCur.get(cur);
    if (!prev || priceRank(p) > priceRank(prev)) byCur.set(cur, p);
  }
  return Array.from(byCur.values()).sort((a, b) => a.currency_code.localeCompare(b.currency_code));
}

function StatusPill({ active }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full"
      style={
        active
          ? { background: "#DDE6D3", color: "#3E5236" }
          : { background: "#EDE0CE", color: "#8B6F4B" }
      }
      data-testid={`mo-packaging-status-${active ? "active" : "inactive"}`}
    >
      <CircleDot size={9} strokeWidth={2.4} /> {active ? "В используемых" : "Не используются"}
    </span>
  );
}

/* ============================== Create/Edit ============================= */
function PackagingDrawer({ open, mode, initial, initialLatestPrice, onClose, onSaved }) {
  const emptyPrice = { price: "", currency_code: "EUR", purchase_quantity: "", purchase_unit: "", effective_at: todayISO(), supplier_name: "", notes: "" };
  const [form, setForm] = useState({ name: "", base_unit: "pcs", category: "", notes: "", is_active: true });
  const [priceForm, setPriceForm] = useState(emptyPrice);
  const [advOpen, setAdvOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAdvOpen(false);
    if (mode === "edit" && initial) {
      setForm({
        name: initial.name || "",
        base_unit: initial.base_unit || "pcs",
        category: initial.category || "",
        notes: initial.notes || "",
        is_active: initial.is_active !== false,
      });
      // Prefill price fields from the latest price so the user recognises the card
      // as ONE record; leaving them alone → no new price row on save.
      if (initialLatestPrice) {
        setPriceForm({
          price: String(initialLatestPrice.price ?? ""),
          currency_code: initialLatestPrice.currency_code || "EUR",
          purchase_quantity: String(initialLatestPrice.purchase_quantity ?? ""),
          purchase_unit: initialLatestPrice.purchase_unit || "",
          effective_at: todayISO(),  // a new update is effective today by default
          supplier_name: "", notes: "",
        });
      } else {
        setPriceForm(emptyPrice);
      }
    } else {
      setForm({ name: "", base_unit: "pcs", category: "", notes: "", is_active: true });
      setPriceForm(emptyPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initial, initialLatestPrice]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setP = (k, v) => setPriceForm((f) => ({ ...f, [k]: v }));

  const family = packagingUnitFamily(form.base_unit);
  const purchaseUnitOptions = PACKAGING_PURCHASE_UNITS_BY_FAMILY[family] || [];
  const currentUnitOk = purchaseUnitOptions.some((u) => u.value === priceForm.purchase_unit);
  if (!currentUnitOk) {
    Promise.resolve().then(() => setP("purchase_unit", purchaseUnitOptions[0]?.value || ""));
  }

  const priceFilled = priceForm.price !== "" && priceForm.purchase_quantity !== "" && priceForm.purchase_unit;
  // In edit mode, only create a new price row if the user actually changed the numbers.
  const priceChanged = !initialLatestPrice
    || String(initialLatestPrice.price) !== String(priceForm.price)
    || String(initialLatestPrice.purchase_quantity) !== String(priceForm.purchase_quantity)
    || initialLatestPrice.purchase_unit !== priceForm.purchase_unit
    || initialLatestPrice.currency_code !== priceForm.currency_code;
  const willCreatePrice = priceFilled && (mode !== "edit" || priceChanged);

  const preview = priceFilled ? livePricePreview({
    price: priceForm.price, qty: priceForm.purchase_quantity,
    unit: priceForm.purchase_unit, currency: priceForm.currency_code,
  }) : null;

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Введите название"); return; }
    if (willCreatePrice) {
      if (Number(priceForm.price) <= 0) { toast.error("«Заплатили» должно быть больше 0"); return; }
      if (Number(priceForm.purchase_quantity) <= 0) { toast.error("«Получили» должно быть больше 0"); return; }
      if (!priceForm.purchase_unit) { toast.error("Выберите единицу"); return; }
      if (!priceForm.effective_at) { toast.error("Укажите дату действия цены"); return; }
    }
    setSaving(true);
    try {
      let saved;
      if (mode === "edit" && initial) {
        const patch = {
          name: form.name.trim(),
          category: form.category.trim() || null,
          notes: form.notes.trim() || null,
          is_active: form.is_active,
        };
        saved = await updatePackagingItem(initial.id, patch);
      } else {
        saved = await createPackagingItem({
          name: form.name.trim(),
          base_unit: form.base_unit,
          category: form.category.trim() || null,
          notes: form.notes.trim() || null,
          is_active: form.is_active,
        });
      }
      let priceOk = true;
      if (willCreatePrice) {
        try {
          await createPackagingPrice({
            packaging_item_id: saved.id,
            price: priceForm.price,
            currency_code: priceForm.currency_code.toUpperCase(),
            purchase_quantity: priceForm.purchase_quantity,
            purchase_unit: priceForm.purchase_unit,
            effective_at: new Date(priceForm.effective_at + "T00:00:00Z").toISOString(),
            supplier_name: priceForm.supplier_name.trim() || null,
            notes: priceForm.notes.trim() || null,
          });
        } catch (e) {
          priceOk = false;
          showErr(e);
          toast.error(mode === "edit"
            ? "Карточку сохранили, но новую цену записать не удалось."
            : "Упаковку создали, но цену записать не удалось. Обновите её из карточки.");
        }
      }
      if (priceOk) toast.success(mode === "edit" ? "Карточка сохранена" : "Упаковка сохранена");
      onSaved(saved);
      onClose();
    } catch (e) { showErr(e); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div className="h-full w-full sm:w-[460px] bg-white p-5 overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "-30px 0 60px -20px rgba(35,23,16,0.4)" }}
           data-testid="mo-packaging-drawer">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {mode === "edit" ? "Редактировать карточку" : "Новая упаковка"}
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Закрыть">
            <X size={16} color="#8B6F4B" />
          </button>
        </div>

        <div className="space-y-3">
          {/* 1. Название */}
          <Field label="Название *">
            <Input value={form.name} onChange={(v) => set("name", v)} testid="mo-packaging-name"
                   placeholder="Коробка 20×20" />
          </Field>
          {/* 2. Базовая единица */}
          <Field label="Базовая единица *">
            <select value={form.base_unit} onChange={(e) => set("base_unit", e.target.value)}
                    disabled={mode === "edit"}
                    className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white disabled:opacity-60"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                    data-testid="mo-packaging-base-unit">
              {PACKAGING_BASE_UNITS.map((u) => (
                <option key={u.value} value={u.value}>{u.value} — {u.label}</option>
              ))}
            </select>
            {mode === "edit" && (
              <p className="text-[11px] mt-1" style={{ color: "#8B6F4B" }}>
                Базовая единица зафиксирована в исторических ценах и калькуляциях.
              </p>
            )}
          </Field>

          {/* 3. Цена — всегда видимая секция */}
          <div className="rounded-lg" style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
            <div className="px-3 pt-2.5 pb-1 text-[11.5px] tracking-[0.18em] uppercase" style={{ color: "#5B4326" }}>
              Цена
            </div>
            <div className="p-3 pt-1 space-y-2.5">
              <div className="grid grid-cols-[1fr_110px] gap-2">
                <Field label="Заплатили *">
                  <Input value={priceForm.price} onChange={(v) => setP("price", v)}
                         testid="mo-packaging-paid-amount" placeholder="24" />
                </Field>
                <Field label="Валюта *">
                  <select value={priceForm.currency_code} onChange={(e) => setP("currency_code", e.target.value)}
                          className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                          data-testid="mo-packaging-paid-currency">
                    {COMMON_CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-[1fr_110px] gap-2">
                <Field label="Получили *">
                  <Input value={priceForm.purchase_quantity} onChange={(v) => setP("purchase_quantity", v)}
                         testid="mo-packaging-received-qty" placeholder="10" />
                </Field>
                <Field label="Единица *">
                  <select value={priceForm.purchase_unit} onChange={(e) => setP("purchase_unit", e.target.value)}
                          className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                          data-testid="mo-packaging-received-unit">
                    {purchaseUnitOptions.map((u) => <option key={u.value} value={u.value}>{u.label} ({u.value})</option>)}
                  </select>
                </Field>
              </div>
              {preview && (
                <div className="rounded-md p-2 text-[12px] tabular-nums"
                     style={{ background: "#FFFFFF", color: "#3E5236", boxShadow: "inset 0 0 0 1px #DFCFA9" }}
                     data-testid="mo-packaging-price-preview">
                  {preview}
                </div>
              )}
              {mode === "edit" && !priceChanged && initialLatestPrice && (
                <p className="text-[11px]" style={{ color: "#8B6F4B" }}>
                  Значения совпадают с последней ценой — новой записи в истории не создадим.
                </p>
              )}
              {mode === "edit" && priceChanged && (
                <p className="text-[11px]" style={{ color: "#5B4326" }}>
                  Это добавит новую запись в историю. Прежние закупки останутся в истории без изменений.
                </p>
              )}
            </div>
          </div>

          {/* 4. Описание */}
          <Field label="Описание">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3}
                      className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none"
                      style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      placeholder="Белая высокая коробка с прозрачным окном"
                      data-testid="mo-packaging-notes" />
          </Field>
          {/* 5. Категория */}
          <Field label="Категория">
            <Input value={form.category} onChange={(v) => set("category", v)} testid="mo-packaging-category"
                   placeholder="Коробки · Подложки · Ленты…" />
          </Field>

          {/* 6. Дополнительно (по умолчанию свернуто) */}
          <div className="rounded-lg" style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
            <button type="button" onClick={() => setAdvOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 text-[12px]"
                    style={{ color: "#5B4326" }}
                    data-testid="mo-packaging-advanced-toggle">
              <span className="tracking-[0.18em] uppercase text-[11.5px]">Дополнительно</span>
              <span>{advOpen ? "▴" : "▾"}</span>
            </button>
            {advOpen && (
              <div className="p-3 pt-0 space-y-2.5">
                <Field label="Цена действует с">
                  <Input value={priceForm.effective_at} onChange={(v) => setP("effective_at", v)}
                         testid="mo-packaging-effective" type="date" />
                  <p className="text-[10.5px] mt-1" style={{ color: "#8B6F4B" }}>
                    По умолчанию — сегодня. Измените дату, если вносите старую закупку или цену на будущую.
                  </p>
                </Field>
                <Field label="Комментарий">
                  <textarea value={priceForm.notes} onChange={(e) => setP("notes", e.target.value)} rows={2}
                            className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none"
                            style={{ background: "#FFFFFF", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                            placeholder="Цена по акции · Стоимость с доставкой…"
                            data-testid="mo-packaging-price-comment" />
                </Field>
                <Field label="Поставщик">
                  <Input value={priceForm.supplier_name} onChange={(v) => setP("supplier_name", v)}
                         testid="mo-packaging-price-supplier" placeholder="Не обязательно" />
                </Field>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none" style={{ color: "#2A1D14" }}>
            <input type="checkbox" checked={form.is_active}
                   onChange={(e) => set("is_active", e.target.checked)}
                   data-testid="mo-packaging-active" />
            <span>В используемых (доступна в калькуляциях)</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6 sticky bottom-0 bg-white pt-3">
          <button onClick={onClose} className="h-10 px-4 rounded-full text-[12.5px]"
                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
            Отменить
          </button>
          <button onClick={submit} disabled={saving}
                  className="h-10 px-5 rounded-full text-[12.5px] disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                  data-testid="mo-packaging-save">
            {saving ? "Сохраняем..." : "Сохранить упаковку"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================== Add Price =============================== */
function AddPackagingPriceDrawer({ open, item, onClose, onCreated }) {
  const family = item ? packagingUnitFamily(item.base_unit) : "count";
  const purchaseUnitOptions = PACKAGING_PURCHASE_UNITS_BY_FAMILY[family] || [];
  const [form, setForm] = useState({
    price: "", currency_code: "EUR", purchase_quantity: "1",
    purchase_unit: "", effective_at: todayISO(), supplier_name: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      price: "", currency_code: "EUR", purchase_quantity: "1",
      purchase_unit: purchaseUnitOptions[0]?.value || "",
      effective_at: todayISO(), supplier_name: "", notes: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  if (!open || !item) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.price || Number(form.price) <= 0) { toast.error("Укажите цену больше 0"); return; }
    if (!form.purchase_quantity || Number(form.purchase_quantity) <= 0) { toast.error("Количество должно быть больше 0"); return; }
    if (!form.purchase_unit) { toast.error("Выберите единицу закупки"); return; }
    if (!form.currency_code || form.currency_code.length !== 3) { toast.error("Валюта: 3-буквенный ISO код"); return; }
    if (!form.effective_at) { toast.error("Укажите дату действия цены"); return; }
    setSaving(true);
    try {
      const payload = {
        packaging_item_id: item.id,
        price: form.price,
        currency_code: form.currency_code.toUpperCase(),
        purchase_quantity: form.purchase_quantity,
        purchase_unit: form.purchase_unit,
        effective_at: new Date(form.effective_at + "T00:00:00Z").toISOString(),
        supplier_name: form.supplier_name.trim() || null,
        notes: form.notes.trim() || null,
      };
      const created = await createPackagingPrice(payload);
      toast.success("Цена добавлена");
      onCreated(created);
      onClose();
    } catch (e) { showErr(e); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div className="h-full w-full sm:w-[440px] bg-white p-5 overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "-30px 0 60px -20px rgba(35,23,16,0.4)" }}
           data-testid="mo-packaging-price-drawer">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            Обновить цену
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Закрыть">
            <X size={16} color="#8B6F4B" />
          </button>
        </div>
        <div className="text-[12px] mb-4" style={{ color: "#8B6F4B" }}>
          {item.name} · базовая единица <b>{packagingUnitLabel(item.base_unit)}</b>
          <div className="mt-1 text-[11px]">
            Новая цена сохранится как отдельная запись — история закупок останется без изменений.
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_110px] gap-2">
            <Field label="Заплатили *">
              <Input value={form.price} onChange={(v) => set("price", v)} testid="mo-packaging-price-amount" placeholder="20.00" />
            </Field>
            <Field label="Валюта *">
              <select value={form.currency_code} onChange={(e) => set("currency_code", e.target.value)}
                      className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-packaging-price-currency">
                {COMMON_CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-[1fr_110px] gap-2">
            <Field label="Получили *">
              <Input value={form.purchase_quantity} onChange={(v) => set("purchase_quantity", v)} testid="mo-packaging-price-qty" placeholder="10" />
            </Field>
            <Field label="Единица *">
              <select value={form.purchase_unit} onChange={(e) => set("purchase_unit", e.target.value)}
                      className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-packaging-price-unit">
                {purchaseUnitOptions.map((u) => <option key={u.value} value={u.value}>{u.label} ({u.value})</option>)}
              </select>
            </Field>
          </div>
          {(() => {
            const preview = livePricePreview({
              price: form.price, qty: form.purchase_quantity,
              unit: form.purchase_unit, currency: form.currency_code,
            });
            return preview ? (
              <div className="rounded-md p-2 text-[12px] tabular-nums"
                   style={{ background: "#F5EBD8", color: "#3E5236", boxShadow: "inset 0 0 0 1px #DFCFA9" }}
                   data-testid="mo-packaging-price-preview">
                {preview}
              </div>
            ) : null;
          })()}
          <p className="text-[11px] -mt-1" style={{ color: "#8B6F4B" }}>
            Совместимость: базовая единица <b>{packagingUnitLabel(item.base_unit)}</b> → доступны{" "}
            {purchaseUnitOptions.map((u) => `${u.label} (${u.value})`).join(" / ")}.
          </p>
          <Field label="Дата действия *">
            <Input value={form.effective_at} onChange={(v) => set("effective_at", v)} testid="mo-packaging-price-effective" type="date" />
          </Field>
          <Field label="Поставщик">
            <Input value={form.supplier_name} onChange={(v) => set("supplier_name", v)} testid="mo-packaging-price-supplier" placeholder="Не обязательно" />
          </Field>
          <Field label="Комментарий к закупке">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
                      className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none"
                      style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      placeholder="Цена по акции · Цена включает доставку…"
                      data-testid="mo-packaging-price-notes" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6 sticky bottom-0 bg-white pt-3">
          <button onClick={onClose} className="h-10 px-4 rounded-full text-[12.5px]"
                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
            Отменить
          </button>
          <button onClick={submit} disabled={saving}
                  className="h-10 px-5 rounded-full text-[12.5px] disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                  data-testid="mo-packaging-price-save">
            {saving ? "Сохраняем..." : "Сохранить новую цену"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================ Detail Drawer ============================= */
function PackagingDetail({ open, item, canWrite, onClose, onEdit, onItemUpdated, onPriceCreated }) {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceOpen, setPriceOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const reload = async () => {
    if (!item) return;
    setLoading(true);
    try {
      const list = await listPackagingPricesByItem(item.id);
      // Same compound-key sort as the list picker so history matches "current".
      list.sort((a, b) => priceRank(b).localeCompare(priceRank(a)));
      setPrices(list);
    } catch (e) { showErr(e); } finally { setLoading(false); }
  };
  useEffect(() => { if (open && item) reload(); /* eslint-disable-line */ }, [open, item?.id]);

  if (!open || !item) return null;
  const latest = latestPricesByCurrency(prices);

  const toggleActive = async () => {
    setToggling(true);
    try {
      const upd = await updatePackagingItem(item.id, { is_active: !item.is_active });
      toast.success(upd.is_active ? "Упаковка возвращена в используемые" : "Упаковка убрана из используемых");
      onItemUpdated(upd);
    } catch (e) { showErr(e); } finally { setToggling(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div className="h-full w-full sm:w-[560px] bg-white overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "-30px 0 60px -20px rgba(35,23,16,0.4)" }}
           data-testid="mo-packaging-detail">
        <div className="p-5 border-b" style={{ borderColor: "#EFE4D0" }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10.5px] tracking-[0.24em] uppercase" style={{ color: "#8B6F4B" }}>Упаковка</div>
              <div className="text-[24px] mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                   data-testid="mo-packaging-detail-name">{item.name}</div>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className="text-[11.5px] px-2 py-0.5 rounded-full" style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  Базовая единица · {packagingUnitLabel(item.base_unit)}
                </span>
                <StatusPill active={item.is_active !== false} />
                {item.category && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#EEE5D2", color: "#5B4326" }}>
                    {item.category}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Закрыть">
              <X size={16} color="#8B6F4B" />
            </button>
          </div>

          {canWrite && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={onEdit} className="h-9 px-4 rounded-full text-[12px]"
                      style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                      data-testid="mo-packaging-detail-edit">
                Редактировать карточку
              </button>
              <button onClick={() => setPriceOpen(true)} className="h-9 px-4 rounded-full text-[12px] inline-flex items-center gap-1.5"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-packaging-detail-add-price">
                <Plus size={12} /> Обновить цену
              </button>
              <button onClick={toggleActive} disabled={toggling}
                      className="h-9 px-4 rounded-full text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60"
                      style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                      data-testid="mo-packaging-detail-toggle">
                {item.is_active ? <><Ban size={12} /> Убрать из используемых</> : <><RotateCcw size={12} /> Вернуть в используемые</>}
              </button>
            </div>
          )}
        </div>

        {item.notes && (
          <div className="px-5 pt-4">
            <div className="text-[10.5px] tracking-[0.24em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Описание</div>
            <div className="text-[12.5px] whitespace-pre-wrap" style={{ color: "#4A3728" }}>{item.notes}</div>
          </div>
        )}

        <div className="p-5">
          <div className="text-[10.5px] tracking-[0.24em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
            {latest.length > 1 ? "Актуальные цены" : "Актуальная цена"}
          </div>
          {latest.length === 0 ? (
            <div className="rounded-lg p-4 text-[12.5px]"
                 style={{ background: "#FBF5EA", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-packaging-detail-latest-empty">
              Цен ещё нет. {canWrite && "Добавьте первую закупку через «Обновить цену», чтобы эта упаковка учитывалась в калькуляциях."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="mo-packaging-detail-latest">
              {latest.map((p) => (
                <div key={p.id} className="rounded-lg p-3"
                     style={{ background: "linear-gradient(180deg,#FBF5EA,#F5EBD8)", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                     data-testid={`mo-packaging-detail-latest-${p.currency_code}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] tracking-[0.16em] uppercase" style={{ color: "#8B6F4B" }}>{p.currency_code}</span>
                    <span className="text-[10px]" style={{ color: "#8B6F4B" }}>с {fmtDate(p.effective_at)}</span>
                  </div>
                  <div className="mt-1 text-[19px] tabular-nums" style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}>
                    {unitPriceLabel(p) || "—"}
                  </div>
                  <div className="text-[11px] mt-0.5 tabular-nums" style={{ color: "#8B6F4B" }}>
                    закупка: {purchaseLabel(p)}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-[10.5px] tracking-[0.24em] uppercase mt-6 mb-2" style={{ color: "#8B6F4B" }}>История цен</div>
          {loading ? (
            <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Загрузка…</div>
          ) : prices.length === 0 ? (
            <div className="text-[12.5px]" style={{ color: "#8B6F4B" }} data-testid="mo-packaging-detail-history-empty">
              История пуста.
            </div>
          ) : (
            <div className="rounded-lg overflow-hidden" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-packaging-detail-history">
              <table className="w-full text-[12px]">
                <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  <tr>
                    <th className="text-left px-3 py-1.5 font-normal">Дата</th>
                    <th className="text-left px-3 py-1.5 font-normal">Цена за единицу</th>
                    <th className="text-left px-3 py-1.5 font-normal">Закупка</th>
                    <th className="text-left px-3 py-1.5 font-normal">Поставщик</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((p) => (
                    <tr key={p.id} className="border-t" style={{ borderColor: "#EFE4D0" }}
                        data-testid={`mo-packaging-detail-history-row-${p.id}`}>
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: "#4A3728" }}>{fmtDate(p.effective_at)}</td>
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: "#2A1D14" }}>{unitPriceLabel(p) || "—"}</td>
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: "#4A3728" }}>{purchaseLabel(p)}</td>
                      <td className="px-3 py-1.5" style={{ color: "#8B6F4B" }}>{p.supplier_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <AddPackagingPriceDrawer open={priceOpen} item={item}
                                 onClose={() => setPriceOpen(false)}
                                 onCreated={(p) => { setPrices((old) => [p, ...old]); onPriceCreated?.(p); }} />
      </div>
    </div>
  );
}

/* =============================== Page =================================== */
export default function MOPackaging() {
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [pricesByItem, setPricesByItem] = useState({});
  const [drawer, setDrawer] = useState({ open: false, mode: "create", item: null });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listPackagingItems();
      setItems(list);
      const priceLists = await Promise.all(list.map((i) => listPackagingPricesByItem(i.id).catch(() => [])));
      const map = {};
      list.forEach((i, k) => { map[i.id] = priceLists[k] || []; });
      setPricesByItem(map);
    } catch (e) { showErr(e); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (aLoad) return;
    if (mode === "no_access" || mode === "demo") { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aLoad, mode]);

  const upsertItem = (updated) => {
    setItems((old) => {
      const idx = old.findIndex((x) => x.id === updated.id);
      if (idx < 0) return [updated, ...old];
      const copy = old.slice(); copy[idx] = updated; return copy;
    });
    setDetail((d) => (d && d.id === updated.id ? updated : d));
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items
      .filter((it) => {
        if (activeFilter === "active" && it.is_active === false) return false;
        if (familyFilter !== "all" && packagingUnitFamily(it.base_unit) !== familyFilter) return false;
        if (!s) return true;
        return (it.name || "").toLowerCase().includes(s) || (it.category || "").toLowerCase().includes(s);
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ru"));
  }, [items, q, activeFilter, familyFilter]);

  const openDetail = async (it) => {
    setDetail(it);
    try {
      const list = await listPackagingPricesByItem(it.id);
      setPricesByItem((old) => ({ ...old, [it.id]: list }));
    } catch (_) {}
  };

  const renderContent = () => {
    if (aLoad || loading) return <LoadingBlock />;
    if (mode === "no_access" || mode === "demo") return <NoAccessScreen />;

    const hasAnything = items.length > 0;

    return (
      <>
        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" color="#8B6F4B" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Поиск по названию или категории..."
                   className="w-full h-10 pl-9 pr-4 rounded-full text-[13px] outline-none bg-white"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-packaging-search" />
          </div>
          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}
                  className="h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-packaging-active-filter">
            <option value="active">Используются</option>
            <option value="all">Все</option>
          </select>
          <select value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}
                  className="h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-packaging-family-filter">
            <option value="all">Все единицы</option>
            <option value="count">Штуки (шт.)</option>
            <option value="length">Длина (см)</option>
            <option value="mass">Масса (г)</option>
            <option value="volume">Объём (мл)</option>
          </select>
          {canWrite && (
            <button onClick={() => setDrawer({ open: true, mode: "create", item: null })}
                    className="h-10 px-5 rounded-full text-[13px] inline-flex items-center gap-2"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-packaging-new">
              <Plus size={15} strokeWidth={1.8} /> Добавить упаковку
            </button>
          )}
        </div>

        {!hasAnything ? (
          <div className="rounded-xl bg-white p-10 text-center"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-packaging-empty">
            <PackageIcon className="mx-auto" size={22} strokeWidth={1.5} color="#8B6F4B" />
            <div className="mt-3 text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              Упаковки пока нет
            </div>
            <p className="text-[12.5px] mt-1.5 max-w-md mx-auto" style={{ color: "#8B6F4B" }}>
              Добавьте первый вид упаковки, чтобы учитывать её в калькуляциях заказов.
            </p>
            {canWrite && (
              <button onClick={() => setDrawer({ open: true, mode: "create", item: null })}
                      className="mt-4 h-10 px-5 rounded-full text-[13px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-packaging-empty-cta">
                <Plus size={14} /> Добавить упаковку
              </button>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center text-[13px]"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#8B6F4B" }} data-testid="mo-packaging-nomatch">
            Ничего не найдено. Попробуйте изменить поиск или фильтр.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <table className="w-full text-[12.5px]" data-testid="mo-packaging-table">
                <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  <tr>
                    <th className="text-left px-3 py-2 font-normal">Упаковка</th>
                    <th className="text-left px-3 py-2 font-normal">Базовая единица</th>
                    <th className="text-left px-3 py-2 font-normal">Последняя цена</th>
                    <th className="text-left px-3 py-2 font-normal">Дата</th>
                    <th className="text-left px-3 py-2 font-normal">Статус</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => {
                    const latest = latestPricesByCurrency(pricesByItem[it.id] || []);
                    return (
                      <tr key={it.id}
                          className="border-t hover:bg-[#FBF5EA] cursor-pointer"
                          style={{ borderColor: "#EFE4D0" }}
                          onClick={() => openDetail(it)}
                          data-testid={`mo-packaging-row-${it.id}`}>
                        <td className="px-3 py-2" style={{ color: "#2A1D14" }}>
                          <div>{it.name}</div>
                          {it.category && (
                            <div className="text-[10.5px] mt-0.5" style={{ color: "#8B6F4B" }}>{it.category}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#4A3728" }}>
                          {packagingUnitLabel(it.base_unit)} <span style={{ color: "#B9A692" }}>({it.base_unit})</span>
                        </td>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#2A1D14" }}>
                          {latest.length === 0 ? (
                            <span style={{ color: "#8B6F4B" }}>—</span>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {latest.map((p) => (
                                <div key={p.id}>
                                  <div>{unitPriceLabel(p)}</div>
                                  <div className="text-[10.5px]" style={{ color: "#8B6F4B" }}>закупка: {purchaseLabel(p)}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#8B6F4B" }}>
                          {latest.length === 0 ? "—" : fmtDate(latest[0].effective_at)}
                        </td>
                        <td className="px-3 py-2"><StatusPill active={it.is_active !== false} /></td>
                        <td className="px-3 py-2 text-right"><ArrowRight size={13} color="#8B6F4B" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2" data-testid="mo-packaging-cards">
              {filtered.map((it) => {
                const latest = latestPricesByCurrency(pricesByItem[it.id] || []);
                return (
                  <button key={it.id} onClick={() => openDetail(it)}
                          className="w-full text-left rounded-xl bg-white p-4 hover:bg-[#FBF5EA] transition"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          data-testid={`mo-packaging-card-${it.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}>{it.name}</div>
                        <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: "#8B6F4B" }}>
                          <span>Базовая · {packagingUnitLabel(it.base_unit)}</span>
                          {it.category && <span>· {it.category}</span>}
                        </div>
                      </div>
                      <StatusPill active={it.is_active !== false} />
                    </div>
                    <div className="mt-3">
                      {latest.length === 0 ? (
                        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>Цен ещё нет</div>
                      ) : (
                        <div className="flex flex-col gap-1 text-[12px] tabular-nums" style={{ color: "#2A1D14" }}>
                          {latest.map((p) => (
                            <div key={p.id} className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "#F5EBD8", color: "#5B4326" }}>{p.currency_code}</span>
                                <span>{unitPriceLabel(p)}</span>
                              </div>
                              <div className="text-[10.5px] inline-flex items-center gap-1" style={{ color: "#8B6F4B" }}>
                                закупка: {purchaseLabel(p)} · <CalendarDays size={10} /> {fmtDate(p.effective_at)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <PackagingDrawer open={drawer.open} mode={drawer.mode} initial={drawer.item}
                         initialLatestPrice={drawer.mode === "edit" && drawer.item
                           ? latestPricesByCurrency(pricesByItem[drawer.item.id] || [])[0] || null
                           : null}
                         onClose={() => setDrawer({ open: false, mode: "create", item: null })}
                         onSaved={(p) => { upsertItem(p); load(); }} />
        <PackagingDetail open={!!detail} item={detail} canWrite={canWrite}
                         onClose={() => setDetail(null)}
                         onEdit={() => setDrawer({ open: true, mode: "edit", item: detail })}
                         onItemUpdated={(upd) => { upsertItem(upd); }}
                         onPriceCreated={(p) => setPricesByItem((old) => ({ ...old, [p.packaging_item_id]: [p, ...(old[p.packaging_item_id] || [])] }))} />
      </>
    );
  };

  return (
    <MOShell active="packaging" title="Упаковка"
             subtitle="Ваш справочник упаковки и историй закупочных цен.">
      {renderContent()}
    </MOShell>
  );
}
